<script setup lang="ts">
import { ref } from 'vue'
import type { CashBookSection } from '@/api/models/reports/CashbookV2'
import type { CashbookRequest } from '@/api/models/reports/CashbookRequest'
import ApiService from '@/api/ApiService'
import ConversionUtility from '@/utils/ConversionUtility'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useToast } from 'primevue/usetoast'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'
import router from '@/router'

const isLoading = ref(false)
const selectedDate = ref<Date>(new Date())
const cashbook = ref<CashBookSection[]>([])
const isDataExist = ref(false)
const toast = useToast()

function getSectionTotals(transactions: any[] | undefined) {
  if (!transactions) return { receiptCash: 0, paymentCash: 0 }

  return transactions.reduce(
    (totals, tx) => ({
      receiptCash: totals.receiptCash + (tx.receipt?.cash || 0),
      paymentCash: totals.paymentCash + (tx.payments?.cash || 0)
    }),
    { receiptCash: 0, paymentCash: 0 }
  )
}

function getAllSectionTotals() {
  return cashbook.value.reduce(
    (totals, section) => {
      const sectionTotal = getSectionTotals(section.transaction)
      return {
        receiptCash: totals.receiptCash + sectionTotal.receiptCash,
        paymentCash: totals.paymentCash + sectionTotal.paymentCash
      }
    },
    { receiptCash: 0, paymentCash: 0 }
  )
}

function handlePrint() {
  const doc = new jsPDF()
  let y = 15

  doc.setFontSize(24)
  doc.text('Bhavasar Pattin Shakari Sanga(R)', 105, y, { align: 'center' })
  doc.setFontSize(16)
  doc.text('Branch: Badami | Tq: Badami | Dist: Bagalkote', 105, y + 10, { align: 'center' })
  y += 25

  doc.setFontSize(18)
  doc.text('Cashbook Report', 14, y)
  doc.setFontSize(12)
  doc.text(`Date: ${selectedDate.value.toLocaleDateString()}`, 150, y)
  y += 10

  cashbook.value.forEach((section) => {
    doc.setFontSize(14)
    doc.text(section.sectionTitle, 14, y)
    y += 5

    const tableBody =
      section.transaction?.map((tx: any) => [
        tx.receipt?.customerId || '-',
        ConversionUtility.toRuppesCoversion(tx.receipt?.cash),
        tx.receipt?.contra || '-',
        tx.receipt?.accountNo || '-',
        tx.particulars?.accountName || '-',
        tx.particulars?.voucherNo || '-',
        ConversionUtility.toRuppesCoversion(tx.payments?.cash),
        tx.payments?.contra || '-'
      ]) ?? []

    autoTable(doc, {
      startY: y,
      head: [
        [
          'Customer ID',
          'Receipt Cash',
          'Receipt Contra',
          'Receipt A/C No',
          'Account Name',
          'Voucher No',
          'Payment Cash',
          'Payment Contra'
        ]
      ],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 2 }
    })

    y = (doc as any).lastAutoTable.finalY + 5

    const sectionTotals = getSectionTotals(section.transaction)
    doc.setFontSize(12)
    doc.text(
      `Subtotal - Receipt: ${ConversionUtility.toRuppesCoversion(sectionTotals.receiptCash)} | Payment: ${ConversionUtility.toRuppesCoversion(sectionTotals.paymentCash)}`,
      14,
      y
    )
    y += 10
  })

  const grandTotals = getAllSectionTotals()
  doc.setFontSize(14)
  doc.setDrawColor(0)
  doc.setFillColor(230, 230, 230)
  doc.rect(12, y, 180, 10, 'F')
  doc.text(
    `Grand Total - Receipt: ${ConversionUtility.toRuppesCoversion(grandTotals.receiptCash)} | Payment: ${ConversionUtility.toRuppesCoversion(grandTotals.paymentCash)}`,
    14,
    y + 7
  )

  doc.autoPrint()
  window.open(doc.output('bloburl'), '_blank')
  doc.save(`Cashbook_Report_${selectedDate.value.toLocaleDateString()}.pdf`)
}

function getCashbook() {
  if (!selectedDate.value) {
    showToast('Error', 'Please select a date.', 'error')
    return
  }

  isLoading.value = true
  const req: CashbookRequest = { date: selectedDate.value.getTime() }

  ApiService.v2cashbook(req)
    .then((response) => {
      if (!response || !response.data) {
        showToast('Error', 'Invalid response from the server.', 'error')
        return
      }
      if (response.statusCode == 200) {
        isDataExist.value = true
        cashbook.value = response.data.section
      }
    })
    .catch((error) => {
      console.error('API Error:', error)
      showToast('Cashbook', error.message || 'An error occurred', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

const goToAbstractedCashbook = () => {
  router.push(
    {name: 'abstractedCashbook'}
  )
}

function showToast(
  summary: string,
  detail: string,
  severity: 'success' | 'info' | 'warn' | 'error'
) {
  toast.add({ severity, summary, detail, life: 5000 })
}
</script>

<template>
  <Toast />
  <main class="main">
    <Card class="h-full overflow-y-scroll pb-20">
      <template #title>
        <h2 class="p-4 text-3xl font-bold">Cashbook</h2>
      </template>

      <template #content>
        <div class="flex p-4 items-center space-x-4">
          <label for="date" class="text-medium font-medium text-gray-700">Day Book : </label>
          <DatePicker
            :maxDate="new Date()"
            v-model="selectedDate"
            dateFormat="dd/mm/yy"
            id="date"
            showIcon
          />
          <CustomButton title="Fetch Cashbook" @click="getCashbook" />
          <CustomButton title="Abstract Cashbook" :outlined="true" @click="goToAbstractedCashbook" />
          <Dialog :visible="isLoading" modal :draggable="false" :closable="false">
            <span>Please wait while we generate your cashbook.</span>
            <ProgressBar mode="indeterminate" style="height: 6px; margin-top: 1.5rem" />
          </Dialog>
        </div>

        <h2 v-if="isDataExist" class="text-1xl font-bold text-center">
          Bhavasar Pattin Shakari Sanga(R), Badami
        </h2>
        <h5 v-if="isDataExist" class="text-center text-sm">
          Branch: Badami | Tq: Badami | Dist: Bagalkote
        </h5>
        <Divider class="mt-4" />

        <div v-for="section in cashbook" :key="section.sectionTitle" class="p-4">
          <DataTable :value="section.transaction" striped-rows>
            <template #header>
              <div class="text-xl font-bold">{{ section.sectionTitle }}</div>
            </template>

            <Column header="Customer ID">
              <template #body="{ data }">{{ data.receipt?.customerId || '-' }}</template>
            </Column>
            <Column header="Receipt Cash">
              <template #body="{ data }">{{
                ConversionUtility.toStringRupeesConversion(data.receipt?.cash)
              }}</template>
            </Column>
            <Column header="Receipt Contra">
              <template #body="{ data }">{{ data.receipt?.contra || '-' }}</template>
            </Column>
            <Column header="Receipt A/C No">
              <template #body="{ data }">{{ data.receipt?.accountNo || '-' }}</template>
            </Column>
            <Column header="Account Name">
              <template #body="{ data }">{{ data.particulars?.accountName || '-' }}</template>
            </Column>
            <Column header="Voucher No">
              <template #body="{ data }">{{ data.particulars?.voucherNo || '-' }}</template>
            </Column>
            <Column header="Payment Cash">
              <template #body="{ data }">{{
                ConversionUtility.toStringRupeesConversion(data.payments?.cash)
              }}</template>
            </Column>
            <Column header="Payment Contra">
              <template #body="{ data }">{{ data.payments?.contra || '-' }}</template>
            </Column>

            <template #footer>
              <tr>
                <td><strong>Subtotal</strong></td>
                <td>
                  <strong>Receipts: {{
                    ConversionUtility.toStringRupeesConversion(
                      getSectionTotals(section.transaction).receiptCash
                    )
                  }} | </strong>
                </td>
                <td colspan="4"></td>
                <td>
                  <strong> Payments: {{
                    ConversionUtility.toStringRupeesConversion(
                      getSectionTotals(section.transaction).paymentCash
                    )
                  }}</strong>
                </td>
                <td></td>
              </tr>
            </template>
          </DataTable>
        </div>

        <Divider class="my-4" />
        <div v-if="isDataExist" class="text-right font-bold pr-6 text-lg">
          Total-<span> Receipt:
          {{ ConversionUtility.toStringRupeesConversion(getAllSectionTotals().receiptCash) }} | </span>
          <span>Payment
          {{ ConversionUtility.toStringRupeesConversion(getAllSectionTotals().paymentCash) }}</span>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end">
          <CustomButton v-if="isDataExist" title="Print" icon="print" @click="handlePrint" />
        </div>
      </template>
    </Card>
  </main>
</template>

<style>
.main {
  padding-bottom: 110px;
}
</style>
